import React from "react";
import { Grid, Box, Typography } from "@mui/material";
import "../../../MotorCalculation/MotorCalculation.scss";

/**
 * The Motor Calculation breakdown card, as shown on the MotorCalculation page.
 *
 * This is the right-hand panel of src/Components/MotorCalculation/Calculations.js
 * lifted out as a pure presentational component. That file cannot be reused
 * directly — it is a full-page panel that owns an input form, its own API calls
 * and a nested export dialog, and is sized for a drawer.
 *
 * Nothing is calculated here. Online Policy runs the SAME engine
 * (calcCarAndBike, via utils/premiumCalculator.js) as the MotorCalculation
 * page, so `calculationResult` already carries every field below and the two
 * screens show identical numbers for identical inputs.
 *
 * Rows are the two-wheeler set (formId 2): the source hides LPG Kit, Seating
 * Capacity, IMT 23, No Claim Bonus and the goods-carrier rows behind vehicle-id
 * guards that a two-wheeler never satisfies.
 *
 * A policy sold by two insurers shows the SAME card: Total A is the OD
 * insurer's side and Total B the TP insurer's, and nothing is added for it.
 */

const BoxStyle = {
  display: "flex",
  width: "100%",
  flexDirection: { xs: "column", md: "row" },
  backgroundColor: "#F5F7F9",
  padding: "10px 20px 0px",
  borderRadius: "10px 10px 0px 0px",
  border: "solid #EAECF0",
  borderWidth: "1px 1px 0px 1px",
};

const BoxStyle1 = {
  width: "100%",
  backgroundColor: "#F5F7F9",
  borderRadius: "0px 0px 10px 10px",
  border: "solid #EAECF0",
  borderWidth: "0 1px 1px 1px",
  overflow: "hidden",
};

const rowStyleLabel = {
  width: "100%",
  height: "100%",
  fontFamily: "inter",
  fontSize: "13px",
  color: "#2b2d42",
  fontWeight: "400",
  textAlign: "center",
};

const rowStyleLabel1 = {
  width: "100%",
  height: "100%",
  fontFamily: "inter",
  fontSize: "16px",
  color: "#000000ff",
  fontWeight: "600",
  textAlign: "center",
};

const rowStyleValue = {
  fontFamily: "inter",
  fontSize: "14px",
  color: "#2b2d42",
  fontWeight: "500",
  textAlign: "center",
};

const rowStyleValue1 = {
  fontFamily: "inter",
  fontSize: "16px",
  color: "#000000ff",
  fontWeight: "600",
  textAlign: "center",
};

const cellStyle = { fontSize: 14, fontWeight: 500, color: "#333" };

const formatTwo = (value) => {
  const num = Number(value ?? 0);
  return Number.isNaN(num) ? "0.00" : num.toFixed(2);
};

/** One row: label on the left, one or two values on the right. */
const Row = ({ label, values, decimals = 2 }) => (
  <Grid
    className="Calculations_Box"
    sx={{
      display: "flex",
      justifyContent: "space-between",
      borderBottom: "1px solid #EAECF0",
      padding: "5px",
    }}
  >
    <Typography className="Box_Label">{label}</Typography>
    {values.length > 1 ? (
      <Box sx={{ display: "flex", gap: 3 }}>
        <Typography sx={cellStyle}>
          {typeof values[0] === "number"
            ? values[0].toFixed(decimals)
            : values[0] ?? 0}
        </Typography>
        <Typography sx={cellStyle}>
          {typeof values[1] === "number" ? values[1].toFixed(2) : values[1] ?? 0}
        </Typography>
      </Box>
    ) : (
      <Typography sx={cellStyle}>
        {typeof values[0] === "number" ? values[0].toFixed(2) : values[0] ?? 0}
      </Typography>
    )}
  </Grid>
);

const FOOTER_LINE = "#EAECF0";
const FINAL_TINT = "#EEF3F9";
const NAVY = "#1E3A5F";

const footerCell = (strong) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: 0,
  px: 1.5,
  py: strong ? 1.5 : 1,
});

/**
 * One footer line: the label and its value share a row, so both halves are
 * always the same height and the lines between rows meet across the middle.
 */
const FooterLine = ({ label, value, testId, strong = false }) => (
  <Box
    sx={{
      display: "grid",
      // On a phone the label gets more room, so "Net Premium [A + B + PA]" stays on one line.
      gridTemplateColumns: { xs: "3fr 2fr", sm: "1fr 1fr" },
      borderTop: strong ? "1px solid #D0D5DD" : "none",
      borderBottom: strong ? "none" : `1px solid ${FOOTER_LINE}`,
      background: strong ? FINAL_TINT : "transparent",
    }}
  >
    <Box sx={{ ...footerCell(strong), borderRight: `1px solid ${FOOTER_LINE}` }}>
      <Typography sx={strong ? { ...rowStyleLabel1, color: NAVY } : rowStyleLabel}>{label}</Typography>
    </Box>
    <Box sx={footerCell(strong)}>
      <Typography data-testid={testId} sx={strong ? { ...rowStyleValue1, color: NAVY } : rowStyleValue}>
        {value}
      </Typography>
    </Box>
  </Box>
);

/**
 * The three lines the calculation ends on: Net Premium [A + B], GST and the
 * Final Premium.
 *
 * Closes the card below, and stands on its own (`standalone`) under the two
 * insurer panels of a Multiple-company policy, so that summary is this block
 * and not a copy of it. A figure passed as null is not known (an old saved
 * premium) and shows as a dash rather than as 0.00.
 *
 * `pa` ({ label, amount }) puts PA Cover on a line of its own above the net.
 * The card never passes it — there PA is the "PA to Owner Driver" row inside
 * Total B, as on the Motor Calculation page; under the two panels it is its
 * own company's, so it is named beside A and B.
 */
export const PremiumFooter = ({ net, gst, final, pa = null, standalone = false }) => {
  const show = (value) => (value === null ? "—" : formatTwo(value));
  const sum = pa ? "A + B + PA" : "A + B";
  return (
    <Box
      sx={
        standalone
          ? { ...BoxStyle1, borderRadius: "10px", borderWidth: "1px" }
          : BoxStyle1
      }
    >
      {pa && <FooterLine label={pa.label} value={show(pa.amount)} testId="premium-pa" />}
      <FooterLine label={`Net Premium [${sum}]`} value={show(net)} testId="premium-net" />
      <FooterLine label={`Gst@18% [${sum}]`} value={show(gst)} testId="premium-gst" />
      <FooterLine label="Final Premium" value={show(final)} testId="premium-final" strong />
    </Box>
  );
};

const MotorCalculationCard = ({ calculationResult }) => {
  const r = calculationResult || {};

  // Numbers come straight from the engine result. `iDV`, `yearOfManufacture`,
  // `cc` and `zone` are echoed back by the calculator from its own input, so
  // the card needs nothing from the form to render the vehicle rows.
  const sectionA = [
    { label: "IDV", values: [r.iDV] },
    { label: "Year of Manufacture", values: [r.yearOfManufacture, "CC"] },
    {
      label: "Vehicle Basic Rate",
      values: [r.vehiclebaseRate, r.vehiclebaseRateValue],
      // The source prints the rate itself to 3 decimals (e.g. 1.760).
      decimals: 3,
    },
    {
      label: "Discount on OD Premium",
      values: [r.discountonODPremium, r.discountonODPremiumValue || 0],
    },
    {
      label: "Basic Od Premium After Discount",
      values: [r.basicODPremiumAfterDiscount || 0],
    },
    { label: "Accessories Value", values: [r.accessories, r.accessoriesValue || 0] },
    { label: "Total Basic Premium", values: [r.totalBasicPremium || 0] },
    { label: "Net Own Damage Premium", values: [r.netOwnDamage || 0] },
    { label: "Zero Dep Premium", values: [r.zeroDepPremium, r.zeroDepPremiumValue || 0] },
    { label: "Total A [OD Premium]", values: [Number(r.totalAodPremium) || 0] },
  ];

  // PA Cover's amount IS the PA to Owner Driver figure the engine already
  // returns (see utils/premiumCalculator.js) — it flows through Total B, Net
  // Premium and GST@18% like any other TP line, so no separate row is needed.
  const sectionB = [
    // Matches the source: the CC is the "label" and the pair reads "Zone | A".
    // This column is third party, so it is the CC third party was priced on:
    // the TP insurer's vehicle when the policy has two.
    { label: r.tpCc ?? r.cc ?? "", values: ["Zone", r.zone ?? ""] },
    { label: "Liapility Premium(TP)", values: [r.LP || 0] },
    { label: "PA to Owner Driver", values: [r.pAOwnerDriver || 0] },
    { label: "PA to Unnamed Passenger", values: [r.pAUnnamedPassenger || 0] },
    { label: "TPPD Restrict", values: [r.tppd || 0] },
    { label: "Total B [TP Premium]", values: [r.totalBodPremium || 0] },
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Grid sx={BoxStyle}>
        <Grid
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            borderRight: { xs: "none", md: "1px solid #EAECF0" },
            borderBottom: { xs: "1px solid #EAECF0", md: "none" },
            paddingRight: { xs: 0, md: "10px" },
            paddingBottom: { xs: "10px", md: 0 },
          }}
        >
          {sectionA.map((row, i) => (
            <Row key={i} {...row} />
          ))}
        </Grid>

        <Grid
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "6px",
            paddingLeft: { xs: 0, md: "10px" },
            paddingTop: { xs: "10px", md: 0 },
          }}
        >
          {sectionB.map((row, i) => (
            <Row key={i} {...row} />
          ))}
        </Grid>
      </Grid>

      <PremiumFooter net={r.netPremiumAB} gst={r.tax} final={r.finalPremium} />
    </Box>
  );
};

export default MotorCalculationCard;
